import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "../lib/data";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { staggerChild } from "../lib/motion";

export default function Portfolio() {
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(PORTFOLIO.map((p) => p.category)))],
    []
  );
  const [active, setActive] = useState("All");
  const items = active === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === active);

  return (
    <>
      <PageHero
        eyebrow="Portfolio"
        title="Recent work we're proud of."
        subtitle="A handful of the businesses we've helped grow — through beautiful websites, smart automation, and AI that actually helps."
      />

      <section className="pb-24" data-testid="portfolio-list">
        <div className="container-page">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="portfolio-filters">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  active === c
                    ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                    : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:border-blue-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <AnimatePresence mode="popLayout">
              {items.map((p) => (
                <motion.div
                  key={p.slug}
                  {...staggerChild}
                  layout
                  exit={{ opacity: 0, y: 20 }}
                >
                  <Link to={`/portfolio/${p.slug}`} className="group block" data-testid={`project-${p.slug}`}>
                    <div className="aspect-[16/11] rounded-[24px] overflow-hidden border border-slate-100 dark:border-slate-800 relative">
                      <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent" />
                      <div className="absolute top-5 left-5 flex gap-2 flex-wrap">
                        <span className="badge-pill bg-white/90 dark:bg-slate-900/90 text-slate-700 dark:text-slate-200 ring-slate-200/70">{p.category}</span>
                        <span className="badge-pill bg-white/90 dark:bg-slate-900/90 text-slate-700 dark:text-slate-200 ring-slate-200/70">{p.year}</span>
                      </div>
                    </div>
                    <div className="mt-6 flex items-start justify-between gap-6">
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors">{p.title}</h3>
                        <p className="mt-2 text-base text-slate-600 dark:text-slate-400 max-w-lg">{p.intro}</p>
                      </div>
                      <ArrowUpRight className="h-6 w-6 text-slate-400 group-hover:text-blue-600 flex-none mt-1 transition-colors" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <CTASection/>
    </>
  );
}
